/**
 * Tabellen im Quelltext als fertige Tabelle anzeigen – solange der Cursor
 * nicht darin steht.
 *
 * Eine breite Markdown-Tabelle (zwölf Spalten, Brüche in jeder Zelle) passt im
 * Quelltext nie in eine Zeile: umbrochen ist sie ein unlesbarer Flickenteppich,
 * ausgerichtet ragt sie seitlich heraus. Gerendert ist dieselbe Tabelle
 * schmal und sauber. Der Text darunter bleibt unverändert – ein Klick auf eine
 * Zelle setzt den Cursor genau in diese Zelle, und die Tabelle erscheint
 * wieder als Quelltext (dann ausgerichtet, wenn sie in die Breite passt,
 * siehe tableAlign.ts).
 *
 * Block-Ersetzungen dürfen in CodeMirror nur aus einem StateField kommen,
 * nicht aus einem ViewPlugin – deshalb hier anders gebaut als die übrigen
 * Dekorationen in codeEditor.ts.
 */

import { StateField, RangeSetBuilder, type EditorState, type Extension, type Text } from "@codemirror/state";
import { Decoration, EditorView, WidgetType, keymap, type DecorationSet } from "@codemirror/view";
import { renderMarkdown } from "@/markdown";
import { icon } from "@/lib/icons";
import { fitTables } from "@/markdown/tableFit";
import { FORMULA_LINE } from "./tableFormulas";

const TABLE_LINE = /^[ \t]*\|/;
const SEP_ROW = /^[ \t]*\|?(?:[ \t]*:?-+:?[ \t]*\|)+[ \t]*(?::?-+:?[ \t]*)?$/;
const FENCE = /^[ \t]*(```|~~~)/;

interface TableSpan {
  from: number;
  to: number;
  /** Quelltext der ganzen Tabelle. */
  source: string;
  /** Formelzeile des Tabellen-Editors direkt darunter (tableFormulas.ts). */
  formulas: { from: number; to: number } | null;
}

/** Tabellen je Dokumentfassung – Cursorbewegungen ändern daran nichts. */
const spanCache = new WeakMap<Text, TableSpan[]>();

function tablesOf(doc: Text): TableSpan[] {
  const cached = spanCache.get(doc);
  if (cached) return cached;
  const out: TableSpan[] = [];
  let inFence = false;
  let n = 1;
  while (n <= doc.lines) {
    const text = doc.line(n).text;
    if (FENCE.test(text)) inFence = !inFence;
    if (inFence || !TABLE_LINE.test(text)) {
      n++;
      continue;
    }
    let last = n;
    while (last < doc.lines && TABLE_LINE.test(doc.line(last + 1).text)) last++;
    // Echte Tabelle nur mit Trennzeile direkt unter dem Kopf – sonst ist es
    // z. B. ein Betrag |x| in einer Formel oder ein Spoiler ||…||.
    if (last > n && SEP_ROW.test(doc.line(n + 1).text)) {
      const from = doc.line(n).from;
      const to = doc.line(last).to;
      // Formelzeile des Tabellen-Editors gehört dazu und verschwindet mit im Widget.
      const below = last < doc.lines ? doc.line(last + 1) : null;
      const formulas = below && FORMULA_LINE.test(below.text.trim()) ? { from: below.from, to: below.to } : null;
      out.push({ from, to: formulas?.to ?? to, source: doc.sliceString(from, to), formulas });
    }
    n = last + 1;
  }
  spanCache.set(doc, out);
  return out;
}

/** Position direkt hinter dem `col`-ten unmaskierten `|` einer Zeile (plus Leerzeichen). */
function cellStart(lineText: string, col: number): number {
  let seen = -1;
  for (let i = 0; i < lineText.length; i++) {
    if (lineText[i] !== "|" || lineText[i - 1] === "\\") continue;
    seen++;
    if (seen === col) return lineText[i + 1] === " " ? i + 2 : i + 1;
  }
  return lineText.length;
}

class TableWidget extends WidgetType {
  constructor(
    private readonly source: string,
    private readonly rows: number,
    private readonly onEdit: () => void
  ) {
    super();
  }

  eq(other: TableWidget): boolean {
    return other.source === this.source;
  }

  get estimatedHeight(): number {
    return this.rows * 34 + 20;
  }

  toDOM(view: EditorView): HTMLElement {
    const el = document.createElement("div");
    el.className = "cm-md-tableWidget markdown-body";
    // Eingerückte Tabellen (in einer Liste) einzeln gerendert sonst als Codeblock.
    el.innerHTML = renderMarkdown(this.source.replace(/^[ \t]+/gm, ""));
    fitTables([el]);
    el.title = "Klicken zum Bearbeiten";
    // Direkt in den Tabellen-Editor – Cursor vorher in diese Tabelle setzen,
    // der Editor sucht sie über die Cursorposition (tableEditor.ts).
    const edit = document.createElement("button");
    edit.type = "button";
    edit.className = "cm-md-tableEditIcon is-widget";
    edit.innerHTML = icon("table", 13);
    edit.title = "Tabelle bearbeiten";
    edit.setAttribute("aria-label", "Tabelle bearbeiten");
    edit.addEventListener("mousedown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      view.dispatch({ selection: { anchor: view.posAtDOM(el) } });
      this.onEdit();
    });
    el.prepend(edit);
    el.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      view.dispatch({ selection: { anchor: this.positionFor(view, el, e.target as Element) } });
      view.focus();
    });
    return el;
  }

  /** Zeile/Spalte der angeklickten Zelle -> Stelle im Quelltext. */
  private positionFor(view: EditorView, el: HTMLElement, target: Element): number {
    const start = view.posAtDOM(el);
    const doc = view.state.doc;
    const firstLine = doc.lineAt(start).number;
    const cell = target.closest("td, th") as HTMLTableCellElement | null;
    const row = cell?.parentElement as HTMLTableRowElement | null;
    if (!cell || !row) return start;
    // Kopfzeile = erste Quellzeile, danach kommt die Trennzeile.
    const rowIndex = row.parentElement?.tagName === "THEAD" ? 0 : row.sectionRowIndex + 1;
    const lineNo = Math.min(doc.lines, firstLine + (rowIndex === 0 ? 0 : rowIndex + 1));
    const line = doc.line(lineNo);
    return line.from + cellStart(line.text, cell.cellIndex);
  }

  ignoreEvent(): boolean {
    return true;
  }
}

/** Statt der kodierten Formelzeile ein kleiner Hinweis – bearbeitet wird sie im Tabellen-Editor. */
class FormulaNote extends WidgetType {
  eq(): boolean {
    return true;
  }
  toDOM(): HTMLElement {
    const el = document.createElement("span");
    el.className = "cm-md-formulaNote";
    el.textContent = "ƒ Formeln – im Tabellen-Editor bearbeiten";
    return el;
  }
}

function build(state: EditorState, onEdit: () => void): DecorationSet {
  const b = new RangeSetBuilder<Decoration>();
  const ranges = state.selection.ranges;
  const touches = (from: number, to: number) => ranges.some((r) => r.from <= to && r.to >= from);
  for (const t of tablesOf(state.doc)) {
    // Cursor in oder direkt an der Tabelle: Quelltext zeigen.
    if (touches(t.from, t.to)) {
      if (t.formulas && !touches(t.formulas.from, t.formulas.to)) {
        b.add(t.formulas.from, t.formulas.to, Decoration.replace({ widget: new FormulaNote() }));
      }
      continue;
    }
    const rows = state.doc.lineAt(t.to).number - state.doc.lineAt(t.from).number;
    b.add(t.from, t.to, Decoration.replace({ widget: new TableWidget(t.source, rows, onEdit), block: true }));
  }
  return b.finish();
}

const tableField = (onEdit: () => void) =>
  StateField.define<DecorationSet>({
    create: (state) => build(state, onEdit),
    update(deco, tr) {
      return tr.docChanged || tr.selection ? build(tr.state, onEdit) : deco;
    },
    provide: (f) => EditorView.decorations.from(f),
  });

/**
 * Pfeil hoch/runter in eine gerenderte Tabelle hinein: CodeMirror springt
 * über eine Block-Ersetzung sonst einfach hinweg, die Tabelle wäre per
 * Tastatur nie zu erreichen.
 */
function enterTable(dir: 1 | -1) {
  return (view: EditorView): boolean => {
    const sel = view.state.selection;
    if (sel.ranges.length > 1 || !sel.main.empty) return false;
    const doc = view.state.doc;
    const line = doc.lineAt(sel.main.head);
    const next = line.number + dir;
    if (next < 1 || next > doc.lines) return false;
    const target = doc.line(next);
    const table = tablesOf(doc).find((t) => (dir === 1 ? t.from === target.from : t.to === target.to));
    if (!table) return false;
    view.dispatch({ selection: { anchor: target.from + cellStart(target.text, 0) }, scrollIntoView: true });
    return true;
  };
}

/** `onEdit` öffnet den visuellen Tabellen-Editor (Knopf am gerenderten Widget). */
export const tableWidgets = (onEdit: () => void): Extension => [
  tableField(onEdit),
  keymap.of([
    { key: "ArrowDown", run: enterTable(1) },
    { key: "ArrowUp", run: enterTable(-1) },
  ]),
];
